import { Info } from '@phosphor-icons/react';
import { DarkPopup } from '@components/index';
import {
  ControlledTextFormField,
  DangerBorderButton,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  SliderFormField,
  StyledButton,
} from '@elements/index';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup } from 'semantic-ui-react';
import {
  CommonUtility,
  decimalMask,
  LocationService,
  ToastMessage,
  tooltipTexts,
  ZoneService,
} from '@utility/index';
import type { ILocationDetails } from 'types/api/location';
import {
  ConfirmDeleteLocationPopup,
  DeleteLocationSchema,
  type TDeleteLocationSchema,
} from '@page-components/warehouse/DeleteLocationPopup';
import { PalletSearchInput } from '@page-components/pallet/PalletSearch';

const LOCATION_STATUS = {
  active: 'active',
  inactive: 'inactive',
  maintenance: 'maintenance',
  reserved: 'reserved',
};

const statusOptions = [
  {
    key: LOCATION_STATUS.active,
    value: LOCATION_STATUS.active,
    text: 'Active',
  },
  {
    key: LOCATION_STATUS.inactive,
    value: LOCATION_STATUS.inactive,
    text: 'Inactive',
  },
  {
    key: LOCATION_STATUS.maintenance,
    value: LOCATION_STATUS.maintenance,
    text: 'Maintenance',
  },
  {
    key: LOCATION_STATUS.reserved,
    value: LOCATION_STATUS.reserved,
    text: 'Reserved',
  },
];

interface IEditLocationFormProps {
  warehouseId?: string;
  isImperial: boolean;
  refreshData: () => void;
  onClose: () => void;
  toggleMoveLocationView: () => void;
  locationData: ILocationDetails | null;
}

const ZoneSchema = yup.object().shape({
  code: yup.string().required('Code is required'),
  status: yup.string(),
  isPickingLocation: yup.boolean(),
  position: yup
    .number()
    .required('Position is required')
    .typeError('Position is required'),
  bay: yup.number().required('Bay is required').typeError('Bay is required'),
  level: yup.number().required('Level is required').typeError('Level is required'),
  priority: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Priority must be an integer')
    .integer('Priority must be an integer'),
  weight: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Priority must be a number'),
  length: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Priority must be a number'),
  width: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Priority must be a number'),
  height: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Priority must be a number'),
});

export function EditLocationForm({
  warehouseId,
  isImperial,
  refreshData,
  onClose,
  locationData,
  toggleMoveLocationView,
}: IEditLocationFormProps) {
  const [loading, setLoading] = useState(false);
  const [currentPallet, setCurrentPallet] = useState<{
    _id?: string;
    palletNumber?: string;
    newData?: boolean;
  } | null>(null);
  const [palletFieldKey, setPalletFieldKey] = useState('');

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [errorObject, setErrorObject] = useState<TDeleteLocationSchema | null>(null);

  const openDeletePopup = () => {
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
  };

  const onDelete = async () => {
    try {
      setLoading(true);
      await ZoneService.removeLocation(locationData?._id);
      ToastMessage.success('Location successfully deleted.');
      refreshData();
      closeDeletePopup();
      onClose();
    } catch (error) {
      const errorObject = DeleteLocationSchema.safeParse(error);
      if (errorObject.success) {
        setErrorObject(errorObject.data);
      } else {
        ToastMessage.apiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(ZoneSchema),
    shouldUnregister: true,
  });

  const isPickingLocation = useWatch({
    control,
    name: 'isPickingLocation',
    defaultValue: false,
  });

  useEffect(() => {
    if (!locationData || Object.keys(locationData).length === 0) {
      return;
    }
    const p0 = locationData?.staticPallets?.[0];
    if (CommonUtility.isValidObject(p0) && p0?.palletNumber) {
      setCurrentPallet({
        _id: p0._id,
        palletNumber: p0.palletNumber,
        newData: false,
      });
    } else {
      setCurrentPallet(null);
    }
    setPalletFieldKey(`${locationData?._id}-${Date.now()}`);
    reset({
      code: locationData?.code,
      status: locationData?.status,
      weight: locationData?.capacity?.maxWeight,
      priority: locationData?.priority,
      ...(locationData?.coordinates || {}),
      ...(locationData?.capacity?.dimensions || {}),
      ...(locationData?.capabilities || {}),
    });
  }, [locationData, reset]);

  const onSubmit = async formData => {
    try {
      setLoading(true);
      let payload = {
        locationId: locationData._id,
        code: formData.code,
        status: formData.status,
        priority: formData.priority,
        capacity: {
          maxWeight: formData.weight,
          dimensions: {
            length: formData.length || '',
            width: formData.width || '',
            height: formData.height || '',
          },
        },
        coordinates: {
          position: formData.position,
          level: formData.level,
          bay: formData.bay,
        },
        capabilities: {
          isPickingLocation: formData.isPickingLocation,
        },
      };
      payload = CommonUtility.removeEmptyFields(payload);
      await ZoneService.updateBay(payload);

      const isStorage = !formData.isPickingLocation;
      const initialPn = locationData?.staticPallets?.[0]?.palletNumber;
      let palletMsg = '';
      if (isStorage && warehouseId) {
        const curPn = currentPallet?.palletNumber;
        const curValid = CommonUtility.isNotEmpty(curPn);
        if (CommonUtility.isNotEmpty(initialPn) && !curValid) {
          await LocationService.configureLocationPallet(locationData._id, {
            palletNumber: initialPn,
            removePallet: true,
          });
          palletMsg = 'Pallet removed from location.';
        } else if (curValid && (curPn !== initialPn || Boolean(currentPallet?.newData))) {
          await LocationService.configureLocationPallet(locationData._id, {
            palletNumber: curPn,
            isNewPallet: Boolean(currentPallet?.newData),
            removePallet: false,
          });
          palletMsg = 'Storage pallet updated.';
        }
      }

      ToastMessage.success(
        palletMsg
          ? `Bay updated successfully. ${palletMsg}`
          : 'Bay updated successfully.',
      );
      onClose();
      refreshData();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup widths="equal" className="mb-0">
          <ControlledTextFormField
            control={control}
            name="code"
            label="Code"
            error={errors?.code}
            hint={errors?.code?.message?.toString() || null}
          />
          <NumberFormField
            control={control}
            name="weight"
            label={`Weight Capacity ${isImperial ? '(lbs)' : '(kg)'}`}
            error={errors?.weight}
            hint={errors.weight?.message?.toString() || null}
          />
        </FormGroup>

        <div className="d-flex align-items-center">
          <SliderFormField
            control={control}
            name="isPickingLocation"
            error={errors?.isPickingLocation}
            hint={errors.isPickingLocation?.message?.toString() || null}
          />
          <span className="mb-3 mr-2">Pickable Location</span>
        </div>

        {!isPickingLocation && warehouseId ? (
          <div className="mb-3">
            <div className="mb-2 font-weight-medium">Storage pallet</div>
            <PalletSearchInput
              key={palletFieldKey}
              warehouseId={warehouseId}
              defaultValue={locationData?.staticPallets?.[0]?.palletNumber || ''}
              onResultClick={p => {
                setCurrentPallet(
                  CommonUtility.isValidObject(p as Record<string, unknown>) &&
                    p?.palletNumber
                    ? p
                    : null,
                );
              }}
              inputClassName="w-100"
              allowCreate
            />
            {(() => {
              const initialPn = locationData?.staticPallets?.[0]?.palletNumber;
              const shouldRemove =
                CommonUtility.isNotEmpty(initialPn) && !currentPallet?.palletNumber;
              return shouldRemove ? (
                <MuteText className="mt-1">
                  Update will remove the pallet from this location. Inventory remains at
                  this location without a pallet link.
                </MuteText>
              ) : null;
            })()}
          </div>
        ) : null}

        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="level"
            label="Level"
            error={errors.level}
            hint={errors.level?.message?.toString() || null}
          />
          <NumberFormField
            control={control}
            name="bay"
            label="Bay"
            error={errors.bay}
            hint={errors.bay?.message?.toString() || null}
          />
          <NumberFormField
            control={control}
            name="position"
            label="Position"
            error={errors.position}
            hint={errors.position?.message?.toString() || null}
          />
        </FormGroup>

        <FormGroup widths="equal">
          <DropdownFormField
            options={statusOptions}
            control={control}
            name="status"
            label="Status"
          />
          <NumberFormField
            control={control}
            name="priority"
            label={
              <div className="d-flex align-items-center">
                <span>Priority</span>
                <DarkPopup trigger={<Info className="ml-1" />}>
                  {tooltipTexts.locationPriorityInfo}
                </DarkPopup>
              </div>
            }
            placeholder="1,2,3..."
            error={errors.priority}
            hint={errors.priority?.message?.toString() || null}
          />
        </FormGroup>

        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="length"
            label={`Length ${isImperial ? '(in.)' : '(cm)'}`}
            maskOptions={decimalMask}
            error={errors.length}
            hint={errors.length?.message?.toString() || null}
          />
          <NumberFormField
            control={control}
            name="width"
            label={`Width ${isImperial ? '(in.)' : '(cm)'}`}
            maskOptions={decimalMask}
            error={errors.width}
            hint={errors.width?.message?.toString() || null}
          />
          <NumberFormField
            control={control}
            name="height"
            label={`Height ${isImperial ? '(in.)' : '(cm)'}`}
            maskOptions={decimalMask}
            error={errors.height}
            hint={errors.height?.message?.toString() || null}
          />
        </FormGroup>
        <div className="d-flex justify-content-between">
          <HoverBorderButton onClick={toggleMoveLocationView} type="button">
            Move Location
          </HoverBorderButton>
          <div className="d-flex">
            <DangerBorderButton type="button" onClick={openDeletePopup}>
              Delete Location
            </DangerBorderButton>
            <HoverBorderButton type="button" onClick={onClose}>
              Close
            </HoverBorderButton>
            <StyledButton type="submit" loading={loading} className="ml-1">
              Update
            </StyledButton>
          </div>
        </div>
      </Form>
      <ConfirmDeleteLocationPopup
        open={showDeletePopup}
        onClose={closeDeletePopup}
        onConfirm={onDelete}
        title={`Delete ${locationData?.code}`}
        message={`Are you sure you want to delete ${locationData?.code}?`}
        loading={loading}
        errorObject={errorObject}
      />
    </>
  );
}
